import React, {useEffect, useRef, useState} from 'react';
import {Box, Stack} from '@mui/material';
import PropTypes from 'prop-types';
import Typography from '@mui/material/Typography';
import RequiredCheckbox from './Shared/RequiredCheckbox';
import RichEditor from './Shared/RichEditor';
import RichEditorResultText from './Shared/RichEditorResultText';
import {stripHtmlTags} from './AdditionalNotes';
import {DateTimePicker} from '@mui/x-date-pickers';
import moment from 'moment/moment';

const DateTime = ({
                      index,
                      id,
                      value,
                      onChange,
                      description,
                      enableValidation,
                      isEditMode,
                      isFormArchived,
                      readonly,
                      placeholderValues,
                      accessLevel,
                      submitMode,
                      minDateTime,
                      maxDateTime,
                      error,
                      audience,
                      recipientType,
                  }) => {
    const [currentValue, setCurrentValue] = useState(description ?? '');
    const [isOpen, setIsOpen] = useState(false);
    const dateTimePickerRef = useRef(null);

    useEffect(() => {
        setCurrentValue(description ?? '');
    }, [description]);

    const handleDescriptionChange = (nextValue) => {
        setCurrentValue(nextValue);

        if (!onChange) return;

        onChange({
            index,
            description: stripHtmlTags(nextValue),
            formattedDescription: nextValue,
        });
    };

    const formatDateTime = (date) => {
        return moment(date).format('D MMM YYYY HH:mm');
    };

    const handleOpen = () => {
        setIsOpen(true);
        setTimeout(() => {
            if (dateTimePickerRef.current) {
                dateTimePickerRef.current.scrollIntoView({
                    block: 'center',
                    inline: 'nearest',
                });
            }
        }, 10);
    };

    const handleClose = () => {
        setIsOpen(false);
    };

    return (
        <Box sx={{mt: 2}}>
            {isEditMode
                ? (
                    <>
                        <RichEditor
                            value={description}
                            onChange={onChange}
                            id={id}
                            index={index}
                            isEditMode={isEditMode}
                            isFormLive={isFormArchived}
                            accessLevel={accessLevel}
                            currentDescription={currentValue}
                            setCurrentDescription={handleDescriptionChange}
                            audience={audience}
                            recipientType={recipientType}
                        />

                        <Stack
                            flexDirection="row"
                            justifyContent="space-between"
                            gap={3}
                            mb={2}
                        >
                            <RequiredCheckbox
                                enableValidation={enableValidation}
                                index={index}
                                onChange={onChange}
                                isFormArchived={isFormArchived}
                            />
                        </Stack>
                        <Stack flexDirection="row" gap={2} mb={2}>
                            <DateTimePicker
                                label="Min Date Time"
                                value={minDateTime ? moment(minDateTime) : null}
                                onChange={(date) =>
                                    onChange({
                                        index,
                                        minDateTime: date ? date.toDate() : undefined,
                                    })
                                }
                                ampm={false}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        error: !!error,
                                        helperText: error,
                                    },
                                }}
                            />
                            <DateTimePicker
                                label="Max Date Time"
                                value={maxDateTime ? moment(maxDateTime) : null}
                                onChange={(date) =>
                                    onChange({
                                        index,
                                        maxDateTime: date ? date.toDate() : undefined,
                                    })
                                }
                                ampm={false}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        error: !!error,
                                        helperText: error,
                                    },
                                }}
                            />
                        </Stack>
                    </>
                )
                : (
                    <>
                        <RichEditorResultText
                            id={id}
                            description={description}
                            placeholderValues={placeholderValues}
                            readonly={readonly}
                            enableValidation={enableValidation}
                        />
                        {!readonly
                            ? (
                                <DateTimePicker
                                    key={id}
                                    ref={dateTimePickerRef}
                                    open={isOpen}
                                    onOpen={handleOpen}
                                    onClose={handleClose}
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            error: !!error,
                                            helperText: error
                                        },
                                        field: {readOnly: true},
                                    }}
                                    format="D MMM YYYY HH:mm"
                                    ampm={false}
                                    required={enableValidation}
                                    onChange={(newValue) => {
                                        onChange({
                                            id,
                                            value: newValue ? formatDateTime(newValue) : null,
                                        });
                                    }}
                                    onAccept={() => {
                                        setIsOpen(false);
                                    }}
                                    disabled={!submitMode}
                                    minDateTime={minDateTime ? moment(minDateTime) : undefined}
                                    maxDateTime={maxDateTime ? moment(maxDateTime) : undefined}
                                />
                            )
                            : (
                                <Typography
                                    sx={{
                                        width: {
                                            xs: '80vw',
                                            sm: '60vw',
                                            md: '45vw',
                                            lg: '45vw',
                                            xl: '45vw',
                                        },
                                        wordWrap: 'break-word',
                                        overflowWrap: 'break-word',
                                    }}
                                >
                                    {value && formatDateTime(value)}
                                </Typography>
                            )}
                    </>
                )}
        </Box>
    );
};

DateTime.propTypes = {
    id: PropTypes.string,
    value: PropTypes.string,
    index: PropTypes.number,
    validation: PropTypes.object,
    description: PropTypes.string,
    onChange: PropTypes.func,
    enableValidation: PropTypes.bool,
    isEditMode: PropTypes.bool,
    isFormArchived: PropTypes.bool,
    readonly: PropTypes.bool,
    placeholderValues: PropTypes.object,
    accessLevel: PropTypes.number,
    submitMode: PropTypes.bool,
    minDateTime: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    maxDateTime: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    error: PropTypes.string,
    audience: PropTypes.number,
    recipientType: PropTypes.string,
    onUpdateDescription: PropTypes.func,
    componentToEditIndex: PropTypes.number,
};

export default DateTime;
